import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  MailOutline as MailOutlineIcon,
  OpenInNew as OpenInNewIcon,
  Sms as SmsIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import SwitchField from '../common/SwitchField';
import PrimaryActionButton from '../common/PrimaryActionButton';
import { ManagementPageCard } from '../layout/ManagementPageLayout';
import StoreService from '../../services/storeService';
import { normalizeLanguage } from '../../constants/Languages';
import { SUPPORT_EMAIL } from '../../constants/support';
import { BRAND } from '../../theme/brandTokens';

const sectionContentPadding = { xs: 2.5, sm: 3, md: 4 };

const innerSectionStyles = {
  p: { xs: 2, sm: 2.5 },
  backgroundColor: BRAND.surface,
  borderRadius: 2,
  border: `1px solid ${BRAND.line}`,
};

const formatCurrencyAmount = (amount, currency, language) => {
  const locale = normalizeLanguage(language) === 'pt'
    ? 'pt-PT'
    : normalizeLanguage(language) === 'es'
      ? 'es-ES'
      : 'en-GB';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: (currency || 'EUR').toUpperCase(),
  }).format(amount);
};

const normalizeSmsSettings = (result) => ({
  enabled: Boolean(result?.enabled ?? result?.Enabled),
  disabledReason: result?.disabledReason ?? result?.DisabledReason ?? 'None',
  unitPrice: result?.unitPrice ?? result?.UnitPrice ?? 0.05,
  currency: result?.currency ?? result?.Currency ?? 'EUR',
  currentPeriodSpendCents: result?.currentPeriodSpendCents ?? result?.CurrentPeriodSpendCents ?? 0,
  monthlySpendingLimitCents: result?.monthlySpendingLimitCents ?? result?.MonthlySpendingLimitCents ?? 3000,
  currentPeriodEndUtc: result?.currentPeriodEndUtc ?? result?.CurrentPeriodEndUtc ?? null,
  canEnable: Boolean(result?.canEnable ?? result?.CanEnable),
  canSend: Boolean(result?.canSend ?? result?.CanSend),
  isTrial: Boolean(result?.isTrial ?? result?.IsTrial),
  allowSmsOnTrial: Boolean(result?.allowSmsOnTrial ?? result?.AllowSmsOnTrial),
});

const StoreSmsBillingSection = ({
  storeId,
  isOwner,
  onBillingPortal,
  billingPortalLoading,
}) => {
  const { t, i18n } = useTranslation();
  const [smsSettings, setSmsSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorKey, setErrorKey] = useState(null);

  const loadSmsSettings = useCallback(async () => {
    if (!storeId || !isOwner) {
      setSmsSettings(null);
      return;
    }

    try {
      setLoading(true);
      const result = await StoreService.getSmsSettings(storeId);
      setSmsSettings(normalizeSmsSettings(result));
      setErrorKey(null);
    } catch (err) {
      console.error('Failed to load SMS settings:', err);
      setSmsSettings(null);
    } finally {
      setLoading(false);
    }
  }, [isOwner, storeId]);

  useEffect(() => {
    loadSmsSettings();
  }, [loadSmsSettings]);

  const spendLabel = useMemo(() => {
    if (!smsSettings) {
      return null;
    }

    const spent = formatCurrencyAmount(
      smsSettings.currentPeriodSpendCents / 100,
      smsSettings.currency,
      i18n.language
    );
    const limit = formatCurrencyAmount(
      smsSettings.monthlySpendingLimitCents / 100,
      smsSettings.currency,
      i18n.language
    );

    return { spent, limit };
  }, [i18n.language, smsSettings]);

  const periodEndLabel = useMemo(() => {
    if (!smsSettings?.currentPeriodEndUtc) {
      return '';
    }

    return format(new Date(smsSettings.currentPeriodEndUtc), 'dd/MM/yyyy');
  }, [smsSettings?.currentPeriodEndUtc]);

  const unitPriceLabel = useMemo(() => {
    if (!smsSettings) {
      return null;
    }

    return formatCurrencyAmount(smsSettings.unitPrice, smsSettings.currency, i18n.language);
  }, [i18n.language, smsSettings]);

  const trialBlocked = Boolean(smsSettings?.isTrial && !smsSettings?.allowSmsOnTrial);
  const trialNeedsStripe = Boolean(
    smsSettings?.isTrial
    && smsSettings?.allowSmsOnTrial
    && !smsSettings?.canEnable
    && !smsSettings?.enabled
  );

  const supportMailto = useMemo(() => {
    const subject = t('paymentSettingsPage.sms.supportEmailSubject');
    const body = t('paymentSettingsPage.sms.supportEmailBody', { storeId: storeId || '' });
    return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, [storeId, t]);

  const enableHelpKey = trialBlocked
    ? 'paymentSettingsPage.sms.enableHelpTrialBlocked'
    : trialNeedsStripe
      ? 'paymentSettingsPage.sms.enableHelpNeedsStripe'
      : 'paymentSettingsPage.sms.enableHelp';

  const handleToggle = async (event) => {
    const enabled = event.target.checked;

    try {
      setSaving(true);
      setErrorKey(null);
      const result = await StoreService.updateSmsSettings(storeId, { enabled });
      setSmsSettings(normalizeSmsSettings(result));
    } catch (err) {
      console.error('Failed to update SMS settings:', err);
      setErrorKey('paymentSettingsPage.sms.error.update');
      await loadSmsSettings();
    } finally {
      setSaving(false);
    }
  };

  if (!isOwner) {
    return null;
  }

  return (
    <ManagementPageCard sx={{ mb: 3 }}>
      <Box sx={{ p: sectionContentPadding }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <SmsIcon sx={{ color: BRAND.accentText }} />
          <Typography variant="h6" sx={{ color: BRAND.accentText, fontWeight: 600 }}>
            {t('paymentSettingsPage.sms.title')}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('paymentSettingsPage.sms.description', { price: unitPriceLabel ?? '€0.05' })}
        </Typography>

        <Box sx={{ ...innerSectionStyles, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} sx={{ color: BRAND.accent }} />
              <Typography variant="body2" color="text.secondary">
                {t('paymentSettingsPage.sms.loading')}
              </Typography>
            </Box>
          ) : (
            <>
              {trialBlocked ? (
                <Alert severity="info">
                  {t('paymentSettingsPage.sms.trialBlocked', { email: SUPPORT_EMAIL })}
                </Alert>
              ) : null}

              {trialNeedsStripe ? (
                <Alert severity="info">
                  {t('paymentSettingsPage.sms.trialNeedsStripe')}
                </Alert>
              ) : null}

              <SwitchField
                checked={Boolean(smsSettings?.enabled)}
                onChange={handleToggle}
                disabled={saving || loading || (!smsSettings?.canEnable && !smsSettings?.enabled)}
                label={t('paymentSettingsPage.sms.enableLabel')}
                helpText={t(enableHelpKey)}
              />

              {trialBlocked ? (
                <Button
                  component="a"
                  href={supportMailto}
                  startIcon={<MailOutlineIcon />}
                  variant="outlined"
                  sx={{
                    alignSelf: { xs: 'stretch', sm: 'flex-start' },
                    borderColor: BRAND.accent,
                    color: BRAND.accentText,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  {t('paymentSettingsPage.sms.emailSupport')}
                </Button>
              ) : null}

              {spendLabel ? (
                <Typography variant="body2" color="text.secondary">
                  {t('paymentSettingsPage.sms.usageSummary', {
                    spent: spendLabel.spent,
                    limit: spendLabel.limit,
                    date: periodEndLabel,
                  })}
                </Typography>
              ) : null}

              {smsSettings?.disabledReason === 'SpendingLimit' ? (
                <Alert severity="warning">
                  {t('paymentSettingsPage.sms.limitReached', {
                    limit: spendLabel?.limit,
                    date: periodEndLabel,
                  })}
                </Alert>
              ) : null}

              {smsSettings?.disabledReason === 'PaymentFailed' ? (
                <Alert severity="error">{t('paymentSettingsPage.sms.paymentFailed')}</Alert>
              ) : null}

              {errorKey ? (
                <Alert severity="error">{t(errorKey)}</Alert>
              ) : null}

              <PrimaryActionButton
                onClick={onBillingPortal}
                disabled={billingPortalLoading}
                startIcon={billingPortalLoading
                  ? <CircularProgress size={16} color="inherit" />
                  : <OpenInNewIcon />}
                sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
              >
                {t('paymentSettingsPage.sms.openBillingPortal')}
              </PrimaryActionButton>
            </>
          )}
        </Box>
      </Box>
    </ManagementPageCard>
  );
};

export default StoreSmsBillingSection;
